import type { DiscoveryV2ListResponseType } from "@sellar-io/api-v2/types";
import { Button, Icon } from "@sellar-io/components";
import { twMerge } from "tailwind-merge";
import { useConsolidatedBasket } from "./useConsolidatedBasket";
import { useMarketBasketFeedback } from "./useMarketBasketFeedback";

const BasketStepper = ({
  quantity,
  onIncrement,
  onDecrement,
  disabled,
  className,
}: {
  quantity: number;
  onIncrement: () => void;
  onDecrement: () => void;
  disabled?: boolean;
  className?: string;
}) => (
  <div
    className={twMerge("flex min-w-33 flex-none items-center gap-1", className)}
  >
    <Button
      aria-label="Decrease quantity"
      className="min-h-9 flex-none px-2.5 shadow-[0_2px_2px_0_rgba(0,0,0,0.15)]"
      disabled={disabled}
      onClick={(event) => {
        event.stopPropagation();
        onDecrement();
      }}
      size="small"
      type="button"
    >
      <Icon icon="MINUS" size={0.85} />
    </Button>
    <span className="flex min-h-9 min-w-6 flex-1 items-center justify-center rounded-lg border border-zinc-300 bg-white px-3 py-1 text-center text-sm font-semibold text-zinc-800">
      {quantity}
    </span>
    <Button
      aria-label="Increase quantity"
      className="min-h-9 flex-none px-2.5 shadow-[0_2px_2px_0_rgba(0,0,0,0.15)]"
      disabled={disabled}
      onClick={(event) => {
        event.stopPropagation();
        onIncrement();
      }}
      size="small"
      type="button"
    >
      <Icon icon="PLUS" size={0.85} />
    </Button>
  </div>
);

export const DiscoveryV2AddToBasketCta = ({
  product,
  className,
}: {
  product: DiscoveryV2ListResponseType[number];
  className?: string;
}) => {
  const { viewBasket } = useMarketBasketFeedback();
  const basket = useConsolidatedBasket({ onOpenBasket: viewBasket });
  const quantity = basket.getItemQuantity(product.id);
  const isMutating = basket.isItemMutating(product.id);

  if (!basket.enabled) return null;

  if (quantity > 0) {
    return (
      <BasketStepper
        className={className}
        disabled={isMutating}
        quantity={quantity}
        onDecrement={() => basket.updateItemQuantity(product, quantity - 1)}
        onIncrement={() => basket.updateItemQuantity(product, quantity + 1)}
      />
    );
  }

  return (
    <Button
      color="purple"
      size="small"
      disabled={!basket.isItemEnabled(product) || isMutating}
      onClick={(event) => {
        event.stopPropagation();
        basket.addItemToBasket(product);
      }}
      className={twMerge(
        "min-w-33 flex-none bg-[#8C23C7] shadow-[0_2px_2px_0_rgba(0,0,0,0.15)]",
        className,
      )}
    >
      <Icon icon="PLUS" size={0.8} />
      {isMutating ? "Adding..." : "Add"}
    </Button>
  );
};
